import { useState } from 'react';
import type { PipelineStep, CompletedStep, RunTestsResult } from '../types';
import { STEP_LABELS } from './constants';
import { PipelineStepRow } from './PipelineStepRow';
import { RunTestsResultsList } from './RunTestsResultsList';

interface PipelineStatusIndicatorProps {
  step: PipelineStep;
  message: string;
  tool?: string;
  modifiedFiles: string[];
  completedSteps: CompletedStep[];
  /**
   * Live-streamed RunTestsResult for the in-flight `runTests` step.
   * When present, the active row becomes expandable to show pending /
   * running / passed / failed test rows that update in place as the
   * pipeline streams `test:progress` events. Cleared in the reducer
   * once the step:end has been folded into a CompletedStep.
   */
  liveTestResults?: RunTestsResult;
}

export function PipelineStatusIndicator({
  step,
  message,
  tool,
  modifiedFiles,
  completedSteps,
  liveTestResults,
}: PipelineStatusIndicatorProps) {
  const label = message || STEP_LABELS[step] || '';
  const showLiveTestResults =
    !!liveTestResults && (step as string) === 'runTests';
  // Default the active row open when there are live test results so the
  // user sees the streaming list without having to click into the row.
  const [expanded, setExpanded] = useState<boolean>(true);
  const isClickable = showLiveTestResults;
  return (
    <div className="agent-pipeline-status" data-testid="agent-pipeline-status" data-step={step}>
      {completedSteps.map((cs, i) => (
        <PipelineStepRow key={i} cs={cs} />
      ))}
      {step !== 'complete' && step !== 'error' && (
        <>
          <div
            className={`pipeline-status-row active${isClickable ? ' clickable' : ''}`}
            onClick={isClickable ? () => setExpanded(!expanded) : undefined}
            style={isClickable ? { cursor: 'pointer' } : undefined}
          >
            {isClickable && (
              <span className="step-expand-icon">{expanded ? '▾' : '▸'}</span>
            )}
            <span className="status-spinner" />
            <span className="pipeline-step-label" data-testid="pipeline-step-label">
              {label}
            </span>
            {tool && <span className="pipeline-tool-badge" data-testid="pipeline-tool-badge">{tool}</span>}
            {showLiveTestResults && liveTestResults!.totalTests !== undefined && (
              <span className="pipeline-step-detail" data-testid="pipeline-live-test-count">
                {liveTestResults!.testsPassed ?? 0}/{liveTestResults!.totalTests}
                {liveTestResults!.testsFailed ? ` · ${liveTestResults!.testsFailed} failed` : ''}
              </span>
            )}
          </div>
          {showLiveTestResults && expanded && (
            <RunTestsResultsList results={liveTestResults!} />
          )}
        </>
      )}
      {modifiedFiles.length > 0 && (
        <div className="pipeline-modified-files" data-testid="pipeline-modified-files">
          {modifiedFiles.map(f => (
            <span key={f} className="pipeline-file-chip">{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}
